import { createSelector } from 'reselect';
import makeMetaKey from '../../../utils/makeMetaKey';
/**
 * The `meta` selector returns the correct meta information for a model
 * depending on the parameters given. Depends on the [meta](#makemetareducer)
 * reducer
 *
 * @param  {Object} options The options for the selector
 * @param  {string} options.model The name of the model to get the item from
 * @param  {string} options.pluralModel The name of the model to get the item from
 * @return {Function} The newly created reselect selector
 * @public
 * @author Connor Taylor
 *
 * @example
 * const state = {
 *   article: {
 *     meta: {
 *       foo: {
 *         loading: false,
 *         loaded: false,
 *         error: null,
 *         next: null
 *       },
 *       foos: {
 *         loading: true,
 *         loaded: false,
 *         error: null,
 *         next: null
 *       },
 *       'foos?featured=true': {
 *         loading: false,
 *         loaded: true,
 *         error: null,
 *         next: `http://api.foo.com/bar?featured=true&page=2`
 *       }
 *     }
 *   }
 * }
 *
 * const meta = makeMetaSelector({ model: `article` });
 * meta(state);
 * // { loading: true, loaded: false, error: null, next: null }
 *
 * meta(state, { params: { featured: true } });
 * // { loading: false, loaded: true, error: null, next: `http://api.foo.com/bar?featured=true&page=2` }
 *
 * meta(state, { batch: false });
 * // { loading: false, loaded: false, error: null, next: null }
 */

var makeMetaSelector = function makeMetaSelector(_ref) {
  var model = _ref.model,
      _ref$pluralModel = _ref.pluralModel,
      pluralModel = _ref$pluralModel === void 0 ? null : _ref$pluralModel;
  return createSelector([function (state, options) {
    if (options === void 0) {
      options = {};
    }

    var _options = options,
        _options$params = _options.params,
        params = _options$params === void 0 ? {} : _options$params,
        _options$batch = _options.batch,
        batch = _options$batch === void 0 ? true : _options$batch;
    var pluralVersion = pluralModel || model + "s";
    var metaModel = batch ? pluralVersion : model;
    var metaKey = makeMetaKey(metaModel, params);
    return state[model].meta[metaKey];
  }], function (meta) {
    return meta || null;
  });
};
/** @component */


export default makeMetaSelector;